import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';

import config from '@config';

import { parseJwt } from '@views/home/auth/Authentication.hooks';
import { AppContext } from '@/App.context';
import {
	ACCESS_TOKEN_EXPIRATION_KEY,
	ACCESS_TOKEN_KEY,
	APP_USER_ID,
	FEATURES_KEY,
	IS_AUTHENTICATED_KEY,
	IS_SUBSCRIBED_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
	USER_ROLE,
} from '@/auth/constants';

export function useCallback(): CallbackResponse {
	const [searchParams] = useSearchParams();
	const [url, setUrl] = useState('');
	const { kv, rebuildGraphQLClient } = useContext(AppContext);
	const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	const code = searchParams.get('code');
	const state = searchParams.get('state');

	useEffect(() => {
		if (!code) {
			setError(new Error('Missing code'));
			setIsLoading(false);
			return;
		}

		const redirecUrl = [
			location.protocol,
			'//',
			location.host,
			location.pathname,
		].join('');

		const params = new URLSearchParams({
			code: `${code}`,
			redirect_uri: redirecUrl,
		});
		const authUrl = [
			config.build.host,
			'/auth/authorize',
			'?',
			params.toString(),
		].join('');
		fetch(authUrl, {
			method: 'GET',
			headers: {
				'app-key': config.build.appKey,
				'Content-Type': 'application/json',
			},
		})
			.then(r => r.json())
			.then(r => {
				saveTokens(r);
				kv.clear();

				if (localStorage.getItem(IS_SUBSCRIBED_KEY) === 'true') {
					setIsSubscribed(true);
				}

				rebuildGraphQLClient();

				setUrl(state ? atob(state) : '/');
				setIsLoading(false);
			})
			.catch(e => {
				setError(e);
				setIsLoading(false);
			});
	}, [code]);

	return {
		isLoading,
		error,
		isSubscribed,
		url,
	};
}

export function saveTokens(content: any) {
	const { access_token, refresh_token, expires } = content;

	// JWT Parse Data
	const accessTokenData = parseJwt(access_token);
	const refreshTokenData = parseJwt(refresh_token);

	// Calculate expiration dates
	const refreshExpirationDate = DateTime.fromSeconds(
		refreshTokenData.exp,
	).toISO();

	clearTokenData();

	// Store in localStorage
	localStorage.setItem(IS_AUTHENTICATED_KEY, 'true');
	localStorage.setItem(USER_ROLE, 'user');
	if (accessTokenData.sub) {
		localStorage.setItem(APP_USER_ID, accessTokenData.sub);
	} else {
		if (localStorage.getItem(APP_USER_ID)) {
			localStorage.removeItem(APP_USER_ID);
		}
	}

	localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
	localStorage.setItem(REFRESH_TOKEN_EXPIRATION_KEY, refreshExpirationDate);

	if (accessTokenData.entitlements) {
		if (accessTokenData.entitlements?.subscription) {
			localStorage.setItem(IS_SUBSCRIBED_KEY, 'true');
		}
		if (accessTokenData.entitlements.features) {
			localStorage.setItem(
				FEATURES_KEY,
				JSON.stringify(accessTokenData.entitlements.features),
			);
		}
	}
}

function clearTokenData() {
	const keys = [
		IS_AUTHENTICATED_KEY,
		USER_ROLE,
		ACCESS_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		REFRESH_TOKEN_KEY,
		REFRESH_TOKEN_EXPIRATION_KEY,
		IS_SUBSCRIBED_KEY,
		FEATURES_KEY,
	];

	for (const key of keys) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
}

export interface CallbackResponse {
	isLoading: boolean;
	error: Error | null;
	isSubscribed?: boolean;
	url: string;
}
